import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import { ConfirmDialog, ContentHeader, Field, Modal, QueryStatus, useToast } from "@cx/ui";
import type { FinancialCategoryView } from "@cx/contracts";
import {
  CATEGORY_KINDS,
  MAX_CATEGORY_DEPTH,
  dreGroupsFor,
  type CategoryKind,
  type DreGroup,
} from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { CATEGORY_KIND_LABEL, DRE_GROUP_LABEL } from "./labels";

type Values = {
  name: string;
  kind: CategoryKind;
  parentId: string | null;
  dreGroup: DreGroup;
  active: boolean;
};

/** Categorias financeiras (RF-FIN-06): plano de contas em árvore (até 3 níveis), cada uma num grupo da DRE. */
export function FinancialCategoriesPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const perms = me.permissions as Permissions;
  const [kind, setKind] = useState<CategoryKind>("revenue");
  const key = ["fin-categories", companyId];
  const list = useQuery({
    queryKey: [...key, kind],
    queryFn: () => api<{ data: FinancialCategoryView[] }>(`/financial-categories?kind=${kind}`),
  });
  const [editing, setEditing] = useState<{ id?: string; values: Values } | null>(null);
  const [deleting, setDeleting] = useState<FinancialCategoryView | null>(null);
  const changed = () => qc.invalidateQueries({ queryKey: key });
  const del = useMutation({
    mutationFn: (id: string) => api(`/financial-categories/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast("success", "Categoria excluída");
      changed();
      qc.invalidateQueries({ queryKey: ["deletions", companyId] });
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const rows = list.data?.data ?? [];
  const blank = (parentId: string | null): Values => ({
    name: "",
    kind,
    parentId,
    dreGroup: rows.find((r) => r.id === parentId)?.dreGroup ?? dreGroupsFor(kind)[0]!,
    active: true,
  });

  return (
    <>
      <ContentHeader
        title="Categorias financeiras"
        breadcrumb={["Cadastros", "Categorias financeiras"]}
        actions={
          can(perms, "financial_categories", "create") && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setEditing({ values: blank(null) })}
            >
              Incluir categoria
            </button>
          )
        }
      />
      <ul className="nav nav-tabs mb-3" role="tablist">
        {CATEGORY_KINDS.map((k) => (
          <li key={k} className="nav-item" role="presentation">
            <button
              type="button"
              role="tab"
              aria-selected={kind === k}
              className={`nav-link${kind === k ? " active" : ""}`}
              onClick={() => setKind(k)}
            >
              {k === "revenue" ? "Receitas" : "Despesas"}
            </button>
          </li>
        ))}
      </ul>
      <div className="box card">
        <QueryStatus loading={list.isLoading} error={list.error} />
        {list.isSuccess && (
          <div className="table-responsive">
            <table className="table align-middle mb-0" aria-label="Categorias financeiras">
              <thead>
                <tr>
                  <th scope="col">Nome</th>
                  <th scope="col">Grupo da DRE</th>
                  <th scope="col">Situação</th>
                  <th scope="col">
                    <span className="visually-hidden">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id}>
                    <td style={{ paddingLeft: `${0.75 + (c.depth - 1) * 1.5}rem` }}>
                      {c.depth > 1 && (
                        <span className="text-muted" aria-hidden="true">
                          └{" "}
                        </span>
                      )}
                      {c.name}
                      {c.isSystem && <span className="badge text-bg-light ms-2">Padrão</span>}
                    </td>
                    <td>{DRE_GROUP_LABEL[c.dreGroup]}</td>
                    <td>{c.active ? "Ativa" : "Inativa"}</td>
                    <td className="text-end text-nowrap">
                      {can(perms, "financial_categories", "create") && c.depth < MAX_CATEGORY_DEPTH && (
                        <button
                          type="button"
                          className="btn btn-sm btn-link me-2"
                          aria-label={`Incluir subcategoria em ${c.name}`}
                          onClick={() => setEditing({ values: blank(c.id) })}
                        >
                          Subcategoria
                        </button>
                      )}
                      {can(perms, "financial_categories", "update") && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary me-2"
                          aria-label={`Editar ${c.name}`}
                          onClick={() =>
                            setEditing({
                              id: c.id,
                              values: {
                                name: c.name,
                                kind: c.kind,
                                parentId: c.parentId,
                                dreGroup: c.dreGroup,
                                active: c.active,
                              },
                            })
                          }
                        >
                          Editar
                        </button>
                      )}
                      {can(perms, "financial_categories", "delete") && !c.isSystem && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          aria-label={`Excluir ${c.name}`}
                          disabled={del.isPending}
                          onClick={() => setDeleting(c)}
                        >
                          Excluir
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {editing && (
        <CategoryDialog
          id={editing.id}
          initial={editing.values}
          parents={rows.filter((r) => r.depth < MAX_CATEGORY_DEPTH && r.id !== editing.id)}
          onClose={() => setEditing(null)}
          onSaved={changed}
        />
      )}
      <ConfirmDialog
        open={!!deleting}
        title="Excluir categoria"
        message={`Excluir a categoria "${deleting?.name ?? ""}"? Ela pode ser restaurada pela Consulta de exclusões.`}
        confirmLabel="Excluir"
        onConfirm={() => {
          if (deleting) del.mutate(deleting.id);
          setDeleting(null);
        }}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}

function CategoryDialog({
  id,
  initial,
  parents,
  onClose,
  onSaved,
}: {
  id?: string;
  initial: Values;
  parents: FinancialCategoryView[];
  onClose(): void;
  onSaved(): void;
}) {
  const { api } = useSession();
  const toast = useToast();
  const [v, setV] = useState(initial);
  const [open, setOpen] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: () => {
      const { kind, ...rest } = v;
      return api(id ? `/financial-categories/${id}` : "/financial-categories", {
        method: id ? "PUT" : "POST",
        body: JSON.stringify(id ? rest : { ...rest, kind }),
      });
    },
    onSuccess: () => {
      toast("success", "Categoria salva");
      onSaved();
      setOpen(false);
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      else if (e instanceof ApiFailure && e.code === "duplicate_category") setErrors({ name: e.message });
      else if (e instanceof ApiFailure && (e.code === "invalid_parent" || e.code === "category_depth"))
        setErrors({ parentId: e.message });
      toast("danger", errorMessage(e));
    },
  });
  return (
    <Modal
      open={open}
      title={id ? "Editar categoria" : `Nova categoria de ${CATEGORY_KIND_LABEL[v.kind].toLowerCase()}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            Salvar
          </button>
        </>
      }
    >
      <div className="row">
        <Field label="Nome" required className="col-12" error={errors.name}>
          <input
            value={v.name}
            maxLength={80}
            data-autofocus
            onChange={(e) => setV({ ...v, name: e.target.value })}
          />
        </Field>
        <Field label="Categoria pai" className="col-md-6" error={errors.parentId}>
          <select
            className="form-select"
            value={v.parentId ?? ""}
            onChange={(e) => setV({ ...v, parentId: e.target.value || null })}
          >
            <option value="">Nenhuma (raiz)</option>
            {parents.map((p) => (
              <option key={p.id} value={p.id}>
                {p.path}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Grupo da DRE" required className="col-md-6" error={errors.dreGroup}>
          <select
            className="form-select"
            value={v.dreGroup}
            onChange={(e) => setV({ ...v, dreGroup: e.target.value as DreGroup })}
          >
            {dreGroupsFor(v.kind).map((g) => (
              <option key={g} value={g}>
                {DRE_GROUP_LABEL[g]}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="form-check">
        <input
          id="cat-active"
          type="checkbox"
          className="form-check-input"
          checked={v.active}
          onChange={(e) => setV({ ...v, active: e.target.checked })}
        />
        <label className="form-check-label" htmlFor="cat-active">
          Ativa
        </label>
      </div>
    </Modal>
  );
}
